import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
    MAT_DIALOG_DATA,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTab, MatTabGroup } from '@angular/material/tabs';

import { tap } from 'rxjs';

import { TiptapEmailReceiptContainerComponent } from '@app/admin/containers/tiptap-email-receipt-container/tiptap-email-receipt-container.component';
import { TiptapEmailRegistrantsContainerComponent } from '@app/admin/containers/tiptap-email-registrants-container/tiptap-email-registrants-container.component';
import { TiptapEmailVariableContainerComponent } from '@app/admin/containers/tiptap-email-variable/tiptap-email-variable-container.component';
import { TiptapGuestLinkContainerComponent } from '@app/admin/containers/tiptap-guest-link-container/tiptap-guest-link-container.component';
import { TiptapImageContainerComponent } from '@app/admin/containers/tiptap-image-container/tiptap-image-container.component';
import { TiptapZoomLinkContainerComponent } from '@app/admin/containers/tiptap-zoom-link-container/tiptap-zoom-link-container.component';
import { createLinkExtension } from '@app/admin/tiptap/link';
import { NotificationActions } from '@app/data/actions';
import { MeetingType } from '@app/shared/enums';
import { ServerResult } from '@app/shared/models';
import { SafeHtmlPipe } from '@app/shared/pipes/safe-html.pipe';
import { createEmailReceiptExtension } from '@app/shared/tiptap/email-receipt';
import { createEmailRegistrantsExtension } from '@app/shared/tiptap/email-registrants';
import { createEmailVariableExtension } from '@app/shared/tiptap/email-variables';
import { Color } from '@app/shared/tiptap/font-colour';
import { FontForeground } from '@app/shared/tiptap/font-foreground';
import { FontSize } from '@app/shared/tiptap/font-size';
import { createGuestLinkExtension } from '@app/shared/tiptap/guest-link';
import { createImageExtension } from '@app/shared/tiptap/image';
import { Indent } from '@app/shared/tiptap/indent';
import { createZoomLinkExtension } from '@app/shared/tiptap/zoom-link';
import { APP_ENVIRONMENT, APP_TIPTAP_EDITOR_RENDER_AS } from '@app/shared/tokens';
import { Store } from '@ngxs/store';
import { Editor } from '@tiptap/core';
import FontFamily from '@tiptap/extension-font-family';
import Highlight from '@tiptap/extension-highlight';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';

const now = new Date();
const SAMPLE_MODEL = {
    userFirstName: '{{userFirstName}}',
    userLastName: '{{userLastName}}',
    userEmail: '{{userEmail}}',
    zoomJoinUrl_12345: '{{ zoomJoinURL }}',
    zoomTopic_12345: '{{ zoomTopic }}',
    zoomStartDate_12345: new Date(
        now.getFullYear(), // Current year
        now.getMonth(), // Current month
        now.getDate() + 3, // Add 3 days
        19, // Set hour to 7:00 PM (19 in 24-hour format)
        0, // Set minutes to 0
        0, // Set seconds to 0
        0, // Set milliseconds to 0
    ),
    zoomRoomId_12345: '3748932121',
    zoomUniqueId_12345: '123',
    event: {
        startDate: new Date(
            now.getFullYear(), // Current year
            now.getMonth(), // Current month
            now.getDate() + 3, // Add 3 days
            19, // Set hour to 7:00 PM (19 in 24-hour format)
            0, // Set minutes to 0
            0, // Set seconds to 0
            0, // Set milliseconds to 0
        ),
    },
    payments: [
        {
            Amount: 22,
            MerchantCurrency: 'CAD',
            PaymentId: '1234567890',
            PaymentDate: new Date(
                now.getFullYear(), // Current year
                now.getMonth(), // Current month
                now.getDate(), // Add 3 days
                19, // Set hour to 7:00 PM (19 in 24-hour format)
                0, // Set minutes to 0
                0, // Set seconds to 0
                0, // Set milliseconds to 0
            ),
            ProductName: '{{ ProductName }}',
            ProductCode: '{{ ProductCode }}',
        },
    ],
    zoomRegistrants: [
        {
            MeetingType: 'meeting',
            RoomId: '3748932121',
            UniqueId: '123',
            Topic: '{{ zoomTopic }}',
            JoinUrl: '{{ zoomJoinURL }}',
            StartDate: new Date(
                now.getFullYear(), // Current year
                now.getMonth(), // Current month
                now.getDate() + 3, // Add 3 days
                19, // Set hour to 7:00 PM (19 in 24-hour format)
                0, // Set minutes to 0
                0, // Set seconds to 0
                0, // Set milliseconds to 0
            ),
        },
    ],
};

@Component({
    selector: 'admin-send-email-preview-dialog',
    templateUrl: './send-email-preview-dialog.component.html',
    styleUrls: ['./send-email-preview-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        ReactiveFormsModule,

        MatFormField,
        MatLabel,
        MatButton,
        MatInput,
        MatIcon,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatDialogClose,
        MatTab,
        MatTabGroup,

        SafeHtmlPipe,
    ],
})
export class SendEmailPreviewDialogComponent implements OnInit {
    fb = inject(FormBuilder);
    injector = inject(Injector);
    http = inject(HttpClient);
    store = inject(Store);
    env = inject(APP_ENVIRONMENT);
    dialogRef = inject(MatDialogRef<SendEmailPreviewDialogComponent>);
    dialogData = inject(MAT_DIALOG_DATA);

    renderResponse: ServerResult<string> | null = null;

    form = this.fb.group({
        to: [this.env.sendToEmail || '', [Validators.required, Validators.email]],
        subject: [this.dialogData.subject, [Validators.required]],
        template: ['', [Validators.required]],
        model: ['', [Validators.required]],
        render: [''],
    });

    ngOnInit(): void {
        const injector = Injector.create({
            providers: [{ provide: APP_TIPTAP_EDITOR_RENDER_AS, useValue: 'email' }],
            parent: this.injector,
        });

        const editor = new Editor({
            editable: false,
            extensions: [
                Indent,
                StarterKit,
                createLinkExtension(injector).configure({ openOnClick: false, linkOnPaste: true, validate: url => true }),
                createImageExtension(injector, TiptapImageContainerComponent),
                createZoomLinkExtension(injector, TiptapZoomLinkContainerComponent),
                createEmailVariableExtension(injector, TiptapEmailVariableContainerComponent),
                createEmailReceiptExtension(injector, TiptapEmailReceiptContainerComponent),
                createEmailRegistrantsExtension(injector, TiptapEmailRegistrantsContainerComponent),
                Underline,
                Highlight,
                Color,
                FontSize,
                TextStyle,
                FontForeground,
                FontFamily,
                // Link.configure({ openOnClick: false, linkOnPaste: true }),

                HorizontalRule,
                TextAlign.configure({
                    types: ['heading', 'paragraph', 'video', 'inlineAudio', 'zoomLink', 'emailReceipt'],
                }),
            ],
        });

        editor.commands.setContent(this.dialogData.template, true);
        const html = editor.getHTML();

        this.form.patchValue({ template: html });

        const model: Record<string, any> = { ...SAMPLE_MODEL };

        // search the html for anything matching 'zoomJoinUrl_81073439711' format
        const regex = /zoomJoinUrl_(\d+)/g;
        const matches = html.match(regex);

        if (matches) {
            matches.forEach(match => {
                model[match] = 'https://zoom.us/j/1234567890?pwd=abc123';
            });
        }

        this.form.patchValue({ model: JSON.stringify(model, null, 4) });

        this.onRender();
    }

    onSend() {
        const { to, subject, template, model } = this.form.value;

        this.http
            .post(`${this.env.serverUrl}/manage/email/send`, { to, subject, template, model: JSON.parse(model || '') })
            .pipe(
                tap(() => {
                    this.store.dispatch(new NotificationActions.Success('Email sent successfully!'));
                }),
            )
            .subscribe();
    }

    onRender() {
        const { template, model } = this.form.value;

        this.http
            .post<ServerResult<string>>(`${this.env.serverUrl}/manage/email/render`, {
                to: '',
                subject: '',
                template,
                model: JSON.parse(model || ''),
            })
            .pipe(
                tap(response => {
                    this.renderResponse = response;
                    this.form.patchValue({ render: response.data });
                }),
            )
            .subscribe();
    }
}
